import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import { CrmAudienceImporter } from "../../Messaging/Campaigns/CrmAudienceImporter";

interface Tag {
  id: number;
  name: string;
  color: string;
}

interface ContactListItem {
  id: number;
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  jobTitle: string;
  lastActivity: string | null;
  dateCreated: string;
  source?: string;
  tags: Tag[];
}

interface ContactsResponse {
  total: number;
  page: number;
  pageSize: number;
  items: ContactListItem[];
}

const PAGE_SIZE = 50;
type SourceFilter = "all" | "chat" | "crm";

const SOURCE_TABS: { value: SourceFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "chat", label: "Chat" },
  { value: "crm", label: "CRM Imported" },
];

function timeAgo(iso: string | null): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function initials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

const AVATAR_COLORS = [
  "bg-blue-500", "bg-violet-500", "bg-emerald-500", "bg-amber-500",
  "bg-rose-500", "bg-cyan-500", "bg-indigo-500", "bg-teal-500"
];

function avatarColor(id: number): string {
  return AVATAR_COLORS[id % AVATAR_COLORS.length];
}

function SourceBadge({ source }: { source?: string }) {
  if (source !== "crm") return null;
  return (
    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200 whitespace-nowrap">
      CRM
    </span>
  );
}

export default function ContactsList() {
  const api = useApi();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");
  const [data, setData] = useState<ContactsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(false);
  const [showImporter, setShowImporter] = useState(false);
  const [importedCount, setImportedCount] = useState<string | null>(null);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(PAGE_SIZE)
      });
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (sourceFilter !== "all") params.set("source", sourceFilter);
      const resp = await api.admin.get(`Contacts?${params}`);
      const json = await resp.json();
      setData(json);
      setStale(false);
    } catch {
      setStale(true);
    } finally {
      setLoading(false);
    }
  }, [api.admin, page, debouncedSearch, sourceFilter]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 1;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-lg font-semibold text-slate-800">Contacts</h1>
          {data && !loading && (
            <p className="text-xs text-slate-500 mt-0.5">
              {data.total.toLocaleString()} contact{data.total !== 1 ? "s" : ""}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {stale && (
            <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
              Showing stale data
            </span>
          )}
          {importedCount && (
            <span className="text-xs text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded">
              ✓ {importedCount}
            </span>
          )}
          <button onClick={() => setShowImporter(true)}
            className="px-3 py-1.5 text-sm font-medium bg-cornflower-blue-500 text-white rounded-lg hover:bg-cornflower-blue-600">
            Import from CRM
          </button>
        </div>
      </div>

      {showImporter && (
        <CrmAudienceImporter
          mode="contacts"
          onClose={() => setShowImporter(false)}
          onImported={(_id, label) => {
            setImportedCount(label);
            setShowImporter(false);
            loadContacts();
            setTimeout(() => setImportedCount(null), 5000);
          }}
        />
      )}

      {/* Search + Source filter */}
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <input
          type="search"
          placeholder="Search by name, email, or company..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-9 px-3 text-sm border border-slate-300 rounded-lg bg-white focus:outline-none focus:border-blue-500 placeholder:text-slate-400 w-72"
        />
        <div className="flex items-center bg-slate-100 rounded-lg p-0.5 gap-0.5">
          {SOURCE_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => { setSourceFilter(tab.value); setPage(1); }}
              className={`text-xs px-3 py-1 rounded-md transition-colors font-medium ${
                sourceFilter === tab.value
                  ? "bg-white text-slate-800 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto border border-slate-200 rounded-lg bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 sticky top-0">
            <tr>
              <th className="px-4 py-2.5 text-left font-medium text-slate-600 text-xs">Name</th>
              <th className="px-4 py-2.5 text-left font-medium text-slate-600 text-xs">Email</th>
              <th className="px-4 py-2.5 text-left font-medium text-slate-600 text-xs">Company</th>
              <th className="px-4 py-2.5 text-left font-medium text-slate-600 text-xs">Last Activity</th>
              <th className="px-4 py-2.5 text-left font-medium text-slate-600 text-xs">Tags</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-400 text-sm">
                  Loading contacts…
                </td>
              </tr>
            ) : !data || data.items.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-slate-400 text-sm">
                  {debouncedSearch ? `No contacts matching "${debouncedSearch}"` : "No contacts yet"}
                </td>
              </tr>
            ) : (
              data.items.map((c) => (
                <tr
                  key={c.id}
                  className="hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/contact_center/contacts/${c.id}`)}
                >
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <div
                        className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-medium shrink-0 ${avatarColor(c.id)}`}
                      >
                        {initials(c.fullName || c.email || "?")}
                      </div>
                      <span className="font-medium text-slate-800">
                        {c.fullName || <span className="text-slate-400 italic">No name</span>}
                      </span>
                      <SourceBadge source={c.source} />
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">{c.email || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-600">
                    {c.company || "—"}
                    {c.jobTitle && (
                      <span className="text-slate-400 text-xs ml-1">· {c.jobTitle}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs whitespace-nowrap">
                    {timeAgo(c.lastActivity)}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {(c.tags ?? []).slice(0, 4).map((t) => (
                        <span
                          key={t.id}
                          className="text-xs px-1.5 py-0.5 rounded font-medium"
                          style={{
                            backgroundColor: t.color ? `${t.color}22` : "#e2e8f0",
                            color: t.color ?? "#475569"
                          }}
                        >
                          {t.name}
                        </span>
                      ))}
                      {(c.tags ?? []).length > 4 && (
                        <span className="text-xs text-slate-400 px-1">+{c.tags.length - 4}</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-3 text-sm text-slate-600">
          <span>
            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, data.total)} of{" "}
            {data.total.toLocaleString()}
          </span>
          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1 border border-slate-300 rounded text-xs disabled:opacity-40 hover:bg-slate-50"
            >
              ← Prev
            </button>
            <span className="text-xs">
              Page {page} of {totalPages}
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1 border border-slate-300 rounded text-xs disabled:opacity-40 hover:bg-slate-50"
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
